import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutline';
import type { AdminUnit } from '../../lib/units-api';
import {
  deleteResident,
  listUnitResidents,
  setPrimaryResident,
  updateResident,
  type PublicResident,
} from '../../lib/residents-api';
import type { UnitBilling } from '../../lib/ops-api';
import { extractApiError } from '../../lib/api-client';
import { formatRupiah } from '../../lib/format';
import { useT } from '../../i18n';
import { labelRepeatsAddress, unitAddress } from '../../lib/unit-address';

const TYPE_KEY = { HOUSE: 'units.typeHouse', APARTMENT: 'units.typeApartment', RUKO: 'units.typeRuko' } as const;
// The UI only tells lived-in from vacant; OWNER_ABSENT (rented out) shows as occupied.
const OCCUPANCY_KEY = { OCCUPIED: 'units.occupiedChip', VACANT: 'units.vacantChip', OWNER_ABSENT: 'units.occupiedChip' } as const;

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' });
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.5, mb: 1 }}
    >
      {children}
    </Typography>
  );
}

/**
 * Everything about one unit in a panel on the right: what it is, who lives
 * there (set primary, suspend, delete), this month's bill, and edit/delete.
 */
export function UnitDrawer({
  unit,
  billing,
  canManageResidents,
  onClose,
  onEdit,
  onDelete,
  onResidentsChanged,
}: {
  unit: AdminUnit | null;
  /** This unit's billing; null when billing isn't available to this user. */
  billing: UnitBilling | null | undefined;
  canManageResidents: boolean;
  onClose: () => void;
  onEdit: (unit: AdminUnit) => void;
  onDelete: (unit: AdminUnit) => void;
  onResidentsChanged: () => void;
}) {
  const t = useT();
  const [residents, setResidents] = useState<PublicResident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; resident: PublicResident } | null>(null);
  const [deleting, setDeleting] = useState<PublicResident | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!unit || !canManageResidents) return;
    setError(null);
    setBusyId(null); // never carry a stuck busy state into a freshly opened unit
    setLoading(true);
    listUnitResidents(unit.id)
      .then(setResidents)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, [unit, canManageResidents]);

  async function run(id: string, action: () => Promise<void>) {
    setBusyId(id);
    setError(null);
    try {
      await action();
      onResidentsChanged();
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  const makePrimary = (r: PublicResident) =>
    run(r.id, async () => { if (unit) setResidents(await setPrimaryResident(unit.id, r.id)); });

  const setStatus = (r: PublicResident, status: 'ACTIVE' | 'SUSPENDED') =>
    run(r.id, async () => {
      const updated = await updateResident(r.id, { status });
      setResidents((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    });

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteResident(deleting.id);
      setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
      setDeleting(null);
      onResidentsChanged();
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
        setDeleting(null);
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  const customLabel = unit && !labelRepeatsAddress(unit.label, unit.block, unit.number);

  return (
    <>
      <Drawer
        anchor="right"
        open={!!unit}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, borderRadius: { sm: '20px 0 0 20px' } } }}
      >
        {unit ? (
          <Stack sx={{ height: '100%' }}>
            {/* Which unit, and what kind. */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 3, pt: 3, pb: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={700}>{unitAddress(unit.block, unit.number)}</Typography>
                {customLabel ? <Typography variant="body2" color="text.secondary">{unit.label}</Typography> : null}
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                  <Chip size="small" variant="outlined" label={t(TYPE_KEY[unit.type])} />
                  <Chip
                    size="small"
                    label={t(OCCUPANCY_KEY[unit.occupancy])}
                    color={unit.occupancy === 'VACANT' ? 'default' : 'success'}
                  />
                </Stack>
              </Box>
              <IconButton onClick={onClose} aria-label={t('common.close')}><CloseIcon /></IconButton>
            </Stack>
            <Divider />

            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
              {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

              {canManageResidents ? (
                <Box sx={{ mb: 3 }}>
                  <SectionTitle>{t('units.drawerResidents')}</SectionTitle>
                  {loading ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
                  ) : residents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('units.noResidentsLinked')}</Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />}>
                      {residents.map((r) => (
                        <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ py: 1.25 }}>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2" fontWeight={600} noWrap>{r.full_name}</Typography>
                              {r.status === 'SUSPENDED' ? <Chip size="small" label={t('res.chipSuspended')} /> : null}
                              {r.status === 'PENDING' ? <Chip size="small" color="warning" variant="outlined" label={t('res.chipPending')} /> : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap component="div">{r.email}</Typography>
                          </Box>
                          {r.is_primary ? (
                            <Tooltip title={t('units.primaryTooltip')}>
                              <Chip size="small" color="primary" icon={<StarIcon fontSize="small" />} label={t('units.primaryChip')} />
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('units.makePrimary')}>
                              <span>
                                <IconButton size="small" onClick={() => makePrimary(r)} disabled={busyId === r.id}>
                                  {busyId === r.id ? <CircularProgress size={16} /> : <StarOutlineIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            aria-label={t('units.more')}
                            onClick={(e) => setMenu({ anchor: e.currentTarget, resident: r })}
                            disabled={busyId === r.id}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                    {t('units.residentsHelper')}
                  </Typography>
                </Box>
              ) : null}

              {billing ? (
                <Box>
                  <SectionTitle>{t('units.drawerBilling')}</SectionTitle>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{t('units.billThisMonth')}</Typography>
                      <Chip
                        size="small"
                        label={t(billing.thisMonth === 'PAID' ? 'units.billPaid' : billing.thisMonth === 'UNPAID' ? 'units.billUnpaid' : 'units.billNone')}
                        color={billing.thisMonth === 'PAID' ? 'success' : billing.thisMonth === 'UNPAID' ? 'error' : 'default'}
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{t('units.arrears')}</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: billing.unpaidCount > 0 ? 'error.main' : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {billing.unpaidCount > 0
                          ? t('units.arrearsValue', { count: billing.unpaidCount, amount: formatRupiah(billing.outstandingCents) })
                          : t('units.noArrears')}
                      </Typography>
                    </Stack>
                    {billing.oldestUnpaidMonth ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('units.arrearsSince', { month: monthLabel(billing.oldestUnpaidMonth) })}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
            </Box>

            {/* Changing the unit itself: edit is common, delete is deliberate. */}
            <Divider />
            <Stack direction="row" spacing={1.5} sx={{ px: 3, py: 2 }}>
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(unit)} sx={{ flexGrow: 1 }}>
                {t('units.editUnit')}
              </Button>
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(unit)}>
                {t('common.delete')}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Drawer>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu?.resident.status === 'SUSPENDED' ? (
          <MenuItem onClick={() => { const r = menu.resident; setMenu(null); void setStatus(r, 'ACTIVE'); }}>
            <ListItemIcon><PlayCircleIcon fontSize="small" /></ListItemIcon>
            {t('res.reactivate')}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { const r = menu!.resident; setMenu(null); void setStatus(r, 'SUSPENDED'); }}>
            <ListItemIcon><PauseCircleIcon fontSize="small" /></ListItemIcon>
            {t('res.suspend')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { const r = menu!.resident; setMenu(null); setDeleteError(null); setDeleting(r); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon>
          {t('common.delete')}
        </MenuItem>
      </Menu>

      <Dialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('res.deleteTitle', { name: deleting?.full_name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">{t('res.deleteWarning')}</Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>{t('common.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
